import React, { useImperativeHandle, forwardRef, useState, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Input, Drawer, Space, Button, App, AutoComplete } from 'antd'
import { addDictItem, modifyDictItem } from '@/api/dictionary'
import { useSearchParams } from 'react-router-dom'

interface EditDictItemProps {
  sourceOptions?: any[]
  onSuccess?: (isEdit: boolean) => void
}

const EditDictItem = (
  { sourceOptions = [], onSuccess }: EditDictItemProps,
  ref: any
) => {
  const [searchParams] = useSearchParams()
  const isControlled = searchParams.get('isControlled')
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [visible, setVisible] = useState(false)
  const [record, setRecord] = useState<any>({})
  const [title, setTitle] = useState('')
  const [form] = Form.useForm()
  const close = () => {
    setRecord({})
    form.resetFields()
    setVisible(false)
  }
  useImperativeHandle(ref, () => ({
    open: (record: any) => {
      setRecord(record)
      form.setFieldsValue(record)
      if (record.id) {
        setTitle(t('ikg.edit'))
      } else {
        setTitle(t('dict.addWord'))
      }
      setVisible(true)
    },
  }))
  const onFinish = async (values: any) => {
    try {
      const isEdit = !!values.id
      if (values.directoryId) {
        values.id = values.directoryId
        delete values.directoryId
        await addDictItem(values)
      } else {
        await modifyDictItem(values)
      }
      close()
      message.success(t('ikg.success', { action: title }))
      typeof onSuccess === 'function' && onSuccess(isEdit)
    } catch (error) {
      close()
    }
  }
  return (
    <Drawer
      title={title}
      width={460}
      open={visible}
      onClose={close}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Space>
            <Button onClick={close}>{t('ikg.cancel')}</Button>
            <Button type="primary" onClick={() => form.submit()}>
              {t('ikg.sure')}
            </Button>
          </Space>
        </div>
      }
    >
      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item label={isControlled ? "code" : t('dict.code')} name="code">
          <Input placeholder={t('ikg.input', { name: isControlled ? "code" : t('dict.code') })} />
        </Form.Item>
        <Form.Item
          label={isControlled ? "CDISC Submission Value" : t('dict.name')}
          name="name"
          rules={[
            {
              required: true,
              message: t('ikg.input', { name: isControlled ? "CDISC Submission Value" : t('dict.name') }),
            },
          ]}
        >
          <Input placeholder={t('ikg.input', { name: isControlled ? "CDISC Submission Value" : t('dict.name') })} />
        </Form.Item>
        {
          isControlled ?
            <Fragment>
              <Form.Item label={"CDISC Synonym(s)"} name="cdiscSynonyms">
                <Input placeholder={t('ikg.input', { name: t('cdiscSynonyms') })} />
              </Form.Item>
              <Form.Item label={"CDISC Definition"} name="cdiscDefinition">
                <Input placeholder={t('ikg.input', { name: t('cdiscDefinition') })} />
              </Form.Item>
              <Form.Item label={"NCI Preferred Term"} name="nciPreferredTerm">
                <Input placeholder={t('ikg.input', { name: t('nciPreferredTerm') })} />
              </Form.Item>
            </Fragment> :
            <Form.Item
              label={t('dict.source')}
              name="source"
              rules={[
                {
                  required: true,
                  message: t('ikg.input', { name: t('dict.source') }),
                },
              ]}
            >
              <AutoComplete
                options={sourceOptions}
                placeholder={t('ikg.input', { name: t('dict.source') })}
                filterOption={(inputValue, option) =>
                  option?.value?.includes(inputValue)
                }
              />
            </Form.Item>
        }

        {record?.directoryId && (
          <Form.Item hidden name="directoryId">
            <Input />
          </Form.Item>
        )}
        {record?.id && (
          <Form.Item hidden name="id">
            <Input />
          </Form.Item>
        )}
        {record?.dictId && (
          <Form.Item hidden name="dictId">
            <Input />
          </Form.Item>
        )}
        <Form.Item hidden name="type">
          <Input />
        </Form.Item>
      </Form>
    </Drawer >
  )
}

export default forwardRef(EditDictItem)
